/**
 * Telling two things apart when they are called the same.
 *
 * <p>A code is unique and a name is not, and the screens show the name because that is what
 * people say out loud. It reads well until the list holds "AVM" twice — which it does,
 * because a code is unique and nothing stops a second type being called the same thing.
 * Choosing between two identical chips is guesswork.</p>
 *
 * <p>So: the name on its own while it is unambiguous, and the code beside it only for the
 * ones that collide. Showing the code on everything would make every list noisier to fix a
 * problem most of them do not have.</p>
 */

/** Something with a name people read and a code that is actually unique. */
export interface Named {
  name: string;
  code: string;
}

/**
 * Labels for a list, each disambiguated only if it needs to be.
 *
 * <p>Returned as a map from id so a caller can label one item without searching the list
 * again for every row it draws.</p>
 */
export function distinctLabels<T extends Named & { id: string }>(items: T[]): Map<string, string> {
  const seen = new Map<string, number>();
  for (const item of items) seen.set(item.name, (seen.get(item.name) ?? 0) + 1);

  return new Map(
    items.map((item) => [
      item.id,
      (seen.get(item.name) ?? 0) > 1 ? `${item.name} · ${item.code}` : item.name,
    ]),
  );
}
